import { useState } from 'react';
import { useAIProviders } from '../hooks/useAIProviders';
import { AIProvider, CreateProviderInput } from '../api/ai.api';
import { 
  Plus, 
  Trash2, 
  Check, 
  X, 
  Bot, 
  Sparkles, 
  Server,
  Loader2,
  Star,
  StarOff,
  TestTube
} from 'lucide-react';

const PROVIDER_ICONS = {
  openai: Sparkles,
  anthropic: Bot,
  custom: Server,
};

const PROVIDER_LABELS = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  custom: 'Custom (OpenAI-compatible)',
};

const DEFAULT_MODELS = {
  openai: ['gpt-4.1', 'gpt-4.1-mini', 'gpt-4.1-nano', 'gpt-4o', 'gpt-4o-mini', 'o3', 'o4-mini'],
  anthropic: [
    'claude-sonnet-4-20250514',
    'claude-opus-4-0-20250514',
    'claude-3-7-sonnet-20250219',
    'claude-3-5-sonnet-20241022',
    'claude-3-5-haiku-20241022',
  ],
  custom: ['llama3', 'llama3:8b', 'mistral', 'mixtral', 'qwen2', 'phi3', 'gemma2'],
};

const DEFAULT_BASE_URLS = {
  ollama: 'http://localhost:11434/v1',
  vllm: 'http://localhost:8000/v1',
  localai: 'http://localhost:8080/v1',
  lmstudio: 'http://localhost:1234/v1',
};

export function ProviderSettings() {
  const { providers, loading, createProvider, updateProvider, deleteProvider, testProvider } = useAIProviders();
  const [showAddModal, setShowAddModal] = useState(false);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<Record<string, { success: boolean; error?: string }>>({});

  const handleTest = async (providerId: string) => {
    setTestingId(providerId);
    try {
      const result = await testProvider(providerId);
      setTestResults(prev => ({ ...prev, [providerId]: result }));
    } catch (err) {
      setTestResults(prev => ({ 
        ...prev, 
        [providerId]: { success: false, error: err instanceof Error ? err.message : 'Test failed' }
      }));
    } finally {
      setTestingId(null);
    }
  };

  const handleSetDefault = async (providerId: string) => {
    await updateProvider(providerId, { isDefault: true });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">AI Provider</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Konfiguriere AI Provider für Smart AI Columns
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Provider hinzufügen
        </button>
      </div>

      {providers.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
          <Bot className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium mb-2">Keine Provider konfiguriert</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Füge einen AI Provider hinzu um Smart AI Columns zu nutzen.
          </p>
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            Ersten Provider hinzufügen
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {providers.map(provider => {
            const Icon = PROVIDER_ICONS[provider.providerType];
            const testResult = testResults[provider.id];
            
            return (
              <div
                key={provider.id}
                className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700"
              >
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <Icon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium">{provider.name}</h3>
                      {provider.isDefault && (
                        <span className="px-2 py-0.5 text-xs bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded-full">
                          Standard
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {PROVIDER_LABELS[provider.providerType]} • {provider.defaultModel}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {testResult && (
                    <div className={`flex items-center gap-1 px-2 py-1 rounded text-sm ${
                      testResult.success 
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                        : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                    }`}>
                      {testResult.success ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <X className="w-4 h-4" />
                      )}
                      {testResult.success ? 'OK' : 'Fehler'}
                    </div>
                  )}
                  
                  <button
                    onClick={() => handleTest(provider.id)}
                    disabled={testingId === provider.id}
                    className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                    title="Verbindung testen"
                  >
                    {testingId === provider.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <TestTube className="w-4 h-4" />
                    )}
                  </button>
                  
                  {!provider.isDefault && (
                    <button
                      onClick={() => handleSetDefault(provider.id)}
                      className="p-2 text-gray-500 hover:text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded-lg transition-colors"
                      title="Als Standard setzen"
                    >
                      <StarOff className="w-4 h-4" />
                    </button>
                  )}
                  
                  <button
                    onClick={() => deleteProvider(provider.id)}
                    className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    title="Löschen"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showAddModal && (
        <AddProviderModal
          onClose={() => setShowAddModal(false)}
          onCreate={async (input) => {
            await createProvider(input);
            setShowAddModal(false);
          }}
        />
      )}
    </div>
  );
}

interface AddProviderModalProps {
  onClose: () => void;
  onCreate: (input: CreateProviderInput) => Promise<void>;
}

function AddProviderModal({ onClose, onCreate }: AddProviderModalProps) {
  const [providerType, setProviderType] = useState<'openai' | 'anthropic' | 'custom'>('openai');
  const [name, setName] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [customBackend, setCustomBackend] = useState<'ollama' | 'vllm' | 'localai' | 'lmstudio' | 'other'>('ollama');
  const [model, setModel] = useState('gpt-4o-mini');
  const [isDefault, setIsDefault] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      await onCreate({
        name: name || PROVIDER_LABELS[providerType],
        providerType,
        apiKey,
        baseUrl: providerType === 'custom' ? baseUrl : undefined,
        defaultModel: model,
        isDefault,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Erstellen');
    } finally {
      setLoading(false);
    }
  };

  const models = DEFAULT_MODELS[providerType];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full p-6">
        <h3 className="text-lg font-semibold mb-4">AI Provider hinzufügen</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Provider Type */}
          <div>
            <label className="block text-sm font-medium mb-2">Provider Typ</label>
            <div className="grid grid-cols-3 gap-2">
              {(['openai', 'anthropic', 'custom'] as const).map(type => {
                const Icon = PROVIDER_ICONS[type];
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => {
                      setProviderType(type);
                      setModel(DEFAULT_MODELS[type][0] || '');
                      if (type === 'custom') {
                        setBaseUrl(DEFAULT_BASE_URLS.ollama);
                        setCustomBackend('ollama');
                        setApiKey('ollama'); // Ollama doesn't need a real key
                      } else {
                        setBaseUrl('');
                        setApiKey('');
                      }
                    }}
                    className={`flex flex-col items-center gap-1 p-3 rounded-lg border transition-colors ${
                      providerType === type
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-xs">{PROVIDER_LABELS[type].split(' ')[0]}</span>
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* Name */}
          <div>
            <label className="block text-sm font-medium mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder={PROVIDER_LABELS[providerType]}
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-transparent"
            />
          </div>
          
          {/* API Key */}
          <div>
            <label className="block text-sm font-medium mb-1">API Key *</label>
            <input
              type="password"
              value={apiKey}
              onChange={e => setApiKey(e.target.value)}
              placeholder={providerType === 'openai' ? 'sk-...' : providerType === 'anthropic' ? 'sk-ant-...' : 'API Key'}
              required
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-transparent"
            />
          </div>
          
          {/* Base URL (for custom providers) */}
          {providerType === 'custom' && (
            <>
              <div>
                <label className="block text-sm font-medium mb-1">Backend</label>
                <div className="grid grid-cols-5 gap-2">
                  {(['ollama', 'vllm', 'localai', 'lmstudio', 'other'] as const).map(backend => (
                    <button
                      key={backend}
                      type="button"
                      onClick={() => {
                        setCustomBackend(backend);
                        if (backend !== 'other') {
                          setBaseUrl(DEFAULT_BASE_URLS[backend]);
                          if (backend === 'ollama') setApiKey('ollama');
                        }
                      }}
                      className={`px-2 py-1.5 text-xs rounded-lg border transition-colors ${
                        customBackend === backend
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {backend === 'ollama' ? 'Ollama' : 
                       backend === 'vllm' ? 'vLLM' : 
                       backend === 'localai' ? 'LocalAI' : 
                       backend === 'lmstudio' ? 'LM Studio' : 'Other'}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Base URL *</label>
                <input
                  type="url"
                  value={baseUrl}
                  onChange={e => setBaseUrl(e.target.value)}
                  placeholder="http://localhost:11434/v1"
                  required
                  className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-transparent"
                />
                {customBackend === 'ollama' && (
                  <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                    ✓ Ollama-Standard vorkonfiguriert (kein API-Key nötig)
                  </p>
                )}
              </div>
            </>
          )}
          
          {/* Model */}
          <div>
            <label className="block text-sm font-medium mb-1">Model *</label>
            {providerType === 'custom' ? (
              <div className="space-y-2">
                <input
                  type="text"
                  value={model}
                  onChange={e => setModel(e.target.value)}
                  placeholder="llama3"
                  required
                  className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-transparent"
                />
                <div className="flex flex-wrap gap-1">
                  {DEFAULT_MODELS.custom.map(m => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setModel(m)}
                      className={`px-2 py-0.5 text-xs rounded border transition-colors ${
                        model === m 
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                      }`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </div>
            ) : models.length > 0 ? (
              <select
                value={model}
                onChange={e => setModel(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-transparent"
              >
                {models.map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={model}
                onChange={e => setModel(e.target.value)}
                placeholder="llama3"
                required
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-transparent"
              />
            )}
          </div>
          
          {/* Default checkbox */}
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={isDefault}
              onChange={e => setIsDefault(e.target.checked)}
              className="rounded"
            />
            <span className="text-sm">Als Standard-Provider setzen</span>
          </label>
          
          {error && (
            <div className="p-3 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg text-sm">
              {error}
            </div>
          )}
          
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={loading || !apiKey || !model}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mx-auto animate-spin" />
              ) : (
                'Hinzufügen'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
